<template>
  <div class="batch-qrcode-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item>批量生成二维码</el-breadcrumb-item>
      </el-breadcrumb>
      <h1>批量生成二维码</h1>
      <p>支持Excel上传或手动输入数据，批量生成二维码并导出为PDF</p>
    </div>

    <!-- 数据输入与配置 -->
    <div class="main-config-section">
      <el-card shadow="hover">
        <div slot="header" class="clearfix">
          <span><i class="el-icon-s-data"></i> 数据配置 ({{ tableData.length }} 条记录)</span>
          <div class="header-buttons">
            <el-button size="small" type="success" icon="el-icon-download" @click="downloadTemplate">
              下载模板
            </el-button>
            <el-button size="small" type="danger" icon="el-icon-delete" @click="clearData" v-if="tableData.length > 0">
              清空数据
            </el-button>
          </div>
        </div>

        <!-- 左右分栏布局 -->
        <el-row :gutter="24">
          <!-- 左侧：数据列表 -->
          <el-col :span="14">
            <div class="data-section">
              <div class="section-header">
                <span class="section-title">
                  <i class="el-icon-edit-outline"></i>
                  数据列表 (点击单元格可直接编辑)
                </span>
                <div class="data-actions">
                  <el-upload class="upload-button" action="#" :auto-upload="false" :on-change="handleExcelUpload"
                    accept=".xlsx,.xls" :show-file-list="false">
                    <el-button type="primary" icon="el-icon-upload2" size="small">
                      上传Excel
                    </el-button>
                  </el-upload>
                  <el-button type="success" icon="el-icon-plus" size="small" @click="addDataRow">
                    添加行
                  </el-button>
                  <el-button type="warning" icon="el-icon-data-board" size="small" @click="loadTestData">
                    载入测试数据
                  </el-button>
                </div>
              </div>

              <template v-if="tableData.length <= 800">
                <el-table :data="tableData" border stripe size="small" style="width: 100%" max-height="600"
                  :empty-text="tableData.length === 0 ? '暂无数据，请上传Excel文件或手动添加数据' : ''" class="data-table">
                  <el-table-column type="index" label="序号" width="60" align="center" fixed="left" />
                  <el-table-column v-for="(field, index) in dataFields" :key="field" :prop="field" :label="field"
                    :min-width="index === 0 ? '200' : '150'" show-overflow-tooltip>
                    <template slot-scope="scope">
                      <el-input v-model="scope.row[field]" size="mini" placeholder="点击编辑" @change="updateTableData"
                        style="border: none; background: transparent;" />
                    </template>
                  </el-table-column>
                  <el-table-column label="操作" width="100" align="center" fixed="right">
                    <template slot-scope="scope">
                      <el-button type="text" size="mini" icon="el-icon-delete" @click="removeRow(scope.$index)" 
                                 style="color: #f56c6c; padding: 5px;">删除</el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </template>
              <template v-else>
                <div style="display:flex; justify-content:space-between; align-items:center; margin:6px 0 4px;">
                  <span style="font-size:12px; color:#909399;">已载入 {{ tableData.length }} 行（虚拟滚动）</span>
                  <el-button size="mini" type="primary" @click="scrollTopVirtual">回到顶部</el-button>
                </div>
                <VirtualTable
                  :rows="tableData"
                  :columns="dataFields"
                  :keeps="40"
                  :row-height="36"
                  :height="480"
                  @cell-change="onVirtualCellChange"
                  @remove-row="removeRow"
                />
              </template>
            </div>
          </el-col>

          <!-- 右侧：生成配置 -->
          <el-col :span="10">
            <div class="config-section">
              <div class="section-header">
                <span class="section-title">
                  <i class="el-icon-setting"></i>
                  生成配置
                </span>
                <el-button type="primary" size="small" icon="el-icon-magic-stick" @click="startGenerate" 
                           :loading="generating || generatingZip" :disabled="qrCodeLabels.length === 0">
                  {{ (generating || generatingZip) ? '制作中...' : '开始制作' }}
                </el-button>
              </div>

              <div class="generation-config">
                <el-form :model="config" label-position="top" size="small">

                  <!-- 页面布局设置（置顶） -->
                  <div class="config-group">
                    <h4 class="group-title">页面布局</h4>
                    
                    <!-- 页面类型选择 -->
                    <el-row :gutter="12">
                      <el-col :span="24">
                        <el-form-item label="生成类型">
                          <el-radio-group v-model="config.generateType" @change="onGenerateTypeChange">
                            <el-radio label="multiA4">多标签A4布局</el-radio>
                            <el-radio label="singlePDF">单页标签（PDF）</el-radio>
                            <el-radio label="singleImage">单页标签（图片）</el-radio>
                          </el-radio-group>
                        </el-form-item>
                      </el-col>
                    </el-row>
                    
                    <!-- A4多标签行列分布 -->
                    <el-row :gutter="12" v-if="config.generateType === 'multiA4'">
                      <el-col :span="16">
                        <el-form-item label="行列分布">
                          <el-row :gutter="8">
                            <el-col :span="10">
                              <el-input-number v-model="config.rows" :min="1" :max="10" controls-position="right"
                                style="width: 100%;" @change="onLayoutChange">
                              </el-input-number>
                            </el-col>
                            <el-col :span="4" style="text-align: center; line-height: 28px; color: #909399;">×</el-col>
                            <el-col :span="10">
                              <el-input-number v-model="config.cols" :min="1" :max="10" controls-position="right"
                                style="width: 100%;" @change="onLayoutChange">
                              </el-input-number>
                            </el-col>
                          </el-row>
                        </el-form-item>
                      </el-col>
                    </el-row>
                    
                    <!-- 单页标签尺寸配置 -->
                    <el-row :gutter="12" v-if="config.generateType === 'singlePDF' || config.generateType === 'singleImage'">
                      <el-col :span="12">
                        <el-form-item label="标签宽度(mm)">
                          <el-input-number v-model="config.singlePageWidth" :min="30" :max="200" controls-position="right"
                            style="width: 100%;" @change="onLayoutChange">
                          </el-input-number>
                        </el-form-item>
                      </el-col>
                      <el-col :span="12">
                        <el-form-item label="标签高度(mm)">
                          <el-input-number v-model="config.singlePageHeight" :min="20" :max="300" controls-position="right"
                            style="width: 100%;" @change="onLayoutChange">
                          </el-input-number>
                        </el-form-item>
                      </el-col>
                    </el-row>
                    
                    <!-- 文件名字段配置（仅单页图片模式显示） -->
                    <el-row :gutter="12" v-if="config.generateType === 'singleImage'">
                      <el-col :span="24">
                        <el-form-item label="文件名字段">
                          <el-select v-model="config.fileNameField" placeholder="请选择用于文件名的数据列" style="width: 100%;" filterable>
                            <el-option v-for="field in dataFields" :key="field" :label="field" :value="field">
                            </el-option>
                          </el-select>
                        </el-form-item>
                      </el-col>
                    </el-row>
                  </div>

                  <!-- 内容配置 -->
                  <div class="config-group">
                    <h4 class="group-title">内容配置</h4>
                    <el-row :gutter="12">
                      <el-col :span="12">
                        <el-form-item label="二维码字段">
                          <el-select v-model="config.qrField" placeholder="请选择" style="width: 100%;" filterable>
                            <el-option v-for="field in dataFields" :key="field" :label="field" :value="field">
                            </el-option>
                          </el-select>
                        </el-form-item>
                      </el-col>
                      <el-col :span="12">
                        <el-form-item label="二维码大小(mm)">
                          <el-input-number v-model="config.qrSize" :min="10" :max="50" controls-position="right"
                            style="width: 100%;">
                          </el-input-number>
                        </el-form-item>
                      </el-col>
                    </el-row>
                    
                    <!-- 二维码高级配置 -->
                    <el-row :gutter="12">
                      <el-col :span="12">
                        <el-form-item label="容错率">
                          <el-select v-model="config.errorCorrectionLevel" style="width: 100%;">
                            <el-option label="低 (L) - 约7%" value="L"></el-option>
                            <el-option label="中 (M) - 约15%" value="M"></el-option>
                            <el-option label="四分位 (Q) - 约25%" value="Q"></el-option>
                            <el-option label="高 (H) - 约30%" value="H"></el-option>
                          </el-select>
                        </el-form-item>
                      </el-col>
                      <el-col :span="12">
                        <el-form-item label="边距(像素)">
                          <el-input-number v-model="config.qrMargin" :min="0" :max="10" controls-position="right"
                            style="width: 100%;">
                          </el-input-number>
                        </el-form-item>
                      </el-col>
                    </el-row>

                    <!-- 文字配置 -->
                    <el-row :gutter="12">
                      <el-col :span="8">
                        <el-form-item>
                          <span slot="label">
                            显示文字
                            <el-switch v-model="config.showText" size="mini"></el-switch>
                          </span>
                        </el-form-item>
                      </el-col>
                      <el-col :span="16" v-if="config.showText">
                        <el-form-item label="文字字段">
                          <el-select v-model="config.textField" placeholder="请选择" style="width: 100%;" filterable>
                            <el-option v-for="field in dataFields" :key="field" :label="field" :value="field">
                            </el-option>
                          </el-select>
                        </el-form-item>
                      </el-col>
                    </el-row>

                    <!-- 文字样式配置 -->
                    <div v-if="config.showText">
                      <el-row :gutter="12">
                        <el-col :span="8">
                          <el-form-item label="文字位置">
                            <el-select v-model="config.textPosition" style="width: 100%;">
                              <el-option label="上方" value="top"></el-option>
                              <el-option label="下方" value="bottom"></el-option>
                              <el-option label="左侧" value="left"></el-option>
                              <el-option label="右侧" value="right"></el-option>
                            </el-select>
                          </el-form-item>
                        </el-col>
                        <el-col :span="8">
                          <el-form-item label="字体大小(mm)">
                            <el-input-number v-model="config.fontSize" :min="2" :max="10" :step="0.5"
                              controls-position="right" style="width: 100%;">
                            </el-input-number>
                          </el-form-item>
                        </el-col>
                        <el-col :span="8">
                          <el-form-item label="文字间距(mm)">
                            <el-input-number v-model="config.textSpacing" :min="0" :max="10" :step="0.5"
                              controls-position="right" style="width: 100%;">
                            </el-input-number>
                          </el-form-item>
                        </el-col>
                      </el-row>
                    </div>
                  </div>

                  <!-- 样式配置 -->
                  <div class="config-group">
                    <h4 class="group-title">样式配置</h4>
                    <el-row :gutter="12">
                      <el-col :span="8">
                        <el-form-item>
                          <span slot="label">
                            显示边框
                            <el-switch v-model="config.showBorder" size="mini"></el-switch>
                          </span>
                        </el-form-item>
                      </el-col>
                      <el-col :span="8" v-if="config.showBorder">
                        <el-form-item label="边框颜色">
                          <el-color-picker v-model="config.borderColor" size="small"></el-color-picker>
                        </el-form-item>
                      </el-col>
                      <el-col :span="8">
                        <el-form-item>
                          <span slot="label">
                            显示背景
                            <el-switch v-model="config.showBackground" size="mini"></el-switch>
                          </span>
                          <el-color-picker v-if="config.showBackground" v-model="config.backgroundColor" size="small" 
                                          style="margin-left: 10px;"></el-color-picker>
                        </el-form-item>
                      </el-col>
                    </el-row>
                  </div>
                </el-form>
              </div>
            </div>
          </el-col>
        </el-row>
      </el-card>
    </div>

    <!-- 预览和生成 -->
    <div class="preview-section">
      <!-- 无数据提示 -->
      <el-card shadow="hover" v-if="qrCodeLabels.length === 0">
        <div style="text-align: center; padding: 40px;">
          <i class="el-icon-info" style="font-size: 48px; color: #909399; margin-bottom: 16px;"></i>
          <h3 style="color: #909399; margin-bottom: 16px;">暂无有效数据</h3>
          <p style="color: #C0C4CC; margin-bottom: 20px;">请填写表格数据或上传Excel文件</p>
          <div>
            <el-button type="primary" icon="el-icon-upload" @click="() => document.querySelector('.upload-button input').click()">
              上传Excel
            </el-button>
            <el-button type="success" icon="el-icon-plus" @click="addDataRow">
              添加数据行
            </el-button>
            <el-button type="info" icon="el-icon-data-board" @click="loadTestData">
              载入示例
            </el-button>
          </div>
        </div>
      </el-card>
      
      <!-- 有数据时的预览 -->
      <el-card shadow="hover" v-if="qrCodeLabels.length > 0">
        <div slot="header" class="clearfix">
          <span><i class="el-icon-view"></i> 标签预览</span>
          <div class="header-buttons">
            <el-button-group size="small" style="margin-right: 10px;">
              <el-button icon="el-icon-arrow-left" :disabled="currentLabelIndex <= 0" @click="prevLabel">
                上一个
              </el-button>
              <el-button icon="el-icon-arrow-right" :disabled="currentLabelIndex >= qrCodeLabels.length - 1" @click="nextLabel">
                下一个
              </el-button>
            </el-button-group>
          </div>
        </div>

        <!-- 预览区域 - 单个标签预览 -->
        <div class="preview-container">
          <div class="single-label-preview" v-if="currentPreviewLabel">
            <div class="qr-label-item" 
                 :style="getSingleLabelStyle()"
                 :class="getLabelClass()">
              
              <!-- 二维码和文字容器 -->
              <div class="qr-content" :style="getContentStyle()">
                <!-- 二维码 -->
                <div class="qr-code-container">
                  <canvas :ref="`previewCanvas`" 
                          :width="getQRCodePixelSize()" 
                          :height="getQRCodePixelSize()"
                          :style="{ width: config.qrSize + 'mm', height: config.qrSize + 'mm' }">
                  </canvas>
                </div>
                
                <!-- 文字 -->
                <div v-if="config.showText && currentPreviewLabel.textContent" 
                     class="qr-text" 
                     :style="getTextStyle()">
                  {{ currentPreviewLabel.textContent }}
                </div>
              </div>
            </div>
            
            <!-- 标签信息 -->
            <div class="label-info">
              <p><strong>标签 {{ currentLabelIndex + 1 }}/{{ qrCodeLabels.length }}</strong></p>
              <p>二维码内容: {{ currentPreviewLabel.qrContent }}</p>
              <p v-if="config.showText">文字内容: {{ currentPreviewLabel.textContent }}</p>
            </div>
          </div>
        </div>

        <div class="page-info">
          共 {{ qrCodeLabels.length }} 个标签
          <span v-if="config.generateType === 'singlePDF'" style="margin-left: 10px; color: #409EFF;">
            (单页PDF: {{ config.singlePageWidth }}×{{ config.singlePageHeight }}mm)
          </span>
          <span v-else-if="config.generateType === 'singleImage'" style="margin-left: 10px; color: #E6A23C;">
            (单页图片: {{ config.singlePageWidth }}×{{ config.singlePageHeight }}mm)
          </span>
          <span v-else style="margin-left: 10px; color: #67C23A;">
            (A4纸 {{ config.rows }}×{{ config.cols }} 布局)
          </span>
        </div>
      </el-card>
    </div>

  <!-- 隐藏的标签容器 - 仅在生成图片ZIP时渲染 -->
  <div v-if="showHiddenForZip" class="hidden-labels-container" style="position: absolute; left: -9999px; top: -9999px;">
      <div v-for="(label, labelIndex) in qrCodeLabels" 
           :key="`hidden-label-${labelIndex}`"
           :ref="`hiddenLabel_${labelIndex}`"
           class="hidden-qr-label-item" 
           :style="getHiddenLabelStyle()"
           :class="getLabelClass()">
        
        <!-- 二维码和文字容器 -->
        <div class="qr-content" :style="getContentStyle()">
          <!-- 二维码 -->
          <div class="qr-code-container">
            <canvas :ref="`hiddenCanvas_${labelIndex}`" 
                    :width="getQRCodePixelSize()" 
                    :height="getQRCodePixelSize()"
                    :style="{ width: config.qrSize + 'mm', height: config.qrSize + 'mm' }">
            </canvas>
          </div>
          
          <!-- 文字 -->
          <div v-if="config.showText && label.textContent" 
               class="qr-text" 
               :style="getTextStyle()">
            {{ label.textContent }}
          </div>
        </div>
      </div>
    </div>
    
    <!-- PDF生成进度弹窗 -->
    <el-dialog
      title="PDF生成进度"
      :visible.sync="showProgress"
      width="400px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      center>
      <div style="text-align: center; padding: 20px;">
        <div style="margin-bottom: 20px; font-size: 16px; color: #606266;">
          正在生成PDF文件，请稍候...
        </div>
        <el-progress 
          :percentage="generateProgress" 
          :status="generateProgress === 100 ? 'success' : null"
          :stroke-width="10">
        </el-progress>
        <div style="margin-top: 20px; color: #909399; font-size: 14px;">
          请勿关闭页面
        </div>
      </div>
    </el-dialog>

    <!-- 图片ZIP生成进度弹窗 -->
    <el-dialog
      title="图片生成进度"
      :visible.sync="showZipProgress"
      width="400px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      center>
      <div style="text-align: center; padding: 20px;">
        <div style="margin-bottom: 20px; font-size: 16px; color: #606266;">
          正在生成图片文件，请稍候...
        </div>
        <el-progress 
          :percentage="zipProgress" 
          :status="zipProgress === 100 ? 'success' : null"
          :stroke-width="10">
        </el-progress>
        <div style="margin-top: 20px; color: #909399; font-size: 14px;">
          请勿关闭页面
        </div>
      </div>
    </el-dialog>

    <!-- Excel 导入进度弹窗 -->
    <el-dialog
      title="Excel导入进度"
      :visible.sync="showImportProgress"
      width="420px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      center>
      <div style="text-align:center; padding:20px;">
        <div style="margin-bottom:16px; font-size:15px; color:#606266;">正在分批导入Excel数据...</div>
        <el-progress :percentage="importProgress" :stroke-width="10" :status="importProgress===100?'success':null" />
        <div style="margin-top:16px; color:#909399; font-size:12px;">大型文件采用分批追加避免界面卡顿</div>
      </div>
    </el-dialog>

  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import { PDFDocument, rgb } from 'pdf-lib'
import fontkit from '@pdf-lib/fontkit'
import QRCode from 'qrcode'
import html2canvas from 'html2canvas'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import sourceHanFontUrl from '@/assets/SourceHanSansCN-Regular.ttf'
import VirtualTable from '@/components/VirtualTable.vue'

export default {
  name: 'BatchQRCodeNew',
  components: { VirtualTable },
  data() {
    return {
      // 性能与增量导入常量（与条形码组件保持一致，可按需调整）
      YIELD_ITEM_INTERVAL: 40,
      YIELD_PAGE_INTERVAL: 2,
      IMPORT_CHUNK_SIZE: 500,
      IMPORT_YIELD_INTERVAL: 1,
      // 数据相关
      tableData: [],
      dataFields: [],
      
      // 配置相关
      config: {
        // 生成类型配置
        generateType: 'multiA4', // 'multiA4': 多标签A4页面, 'singlePDF': 单页标签PDF, 'singleImage': 单页标签图片
        
        // A4多标签配置
        rows: 3,
        cols: 3,
        
        // 单页标签尺寸配置
        singlePageWidth: 100, // mm
        singlePageHeight: 60, // mm
        
        // 内容配置
        qrField: '',
        showText: true,
        textField: '',
        textPosition: 'bottom',
        fontSize: 3,
        textSpacing: 2,
        qrSize: 20,
        errorCorrectionLevel: 'M', // 二维码容错率
        qrMargin: 1, // 二维码边距
        showBorder: false,
        borderColor: '#000000',
        showBackground: false,
        backgroundColor: '#ffffff',
        
        // 图片文件名配置（仅单页图片模式使用）
        fileNameField: '' // 文件名字段
      },
      
      // 标签相关
      qrCodeLabels: [],
      qrCodePages: [],
      currentLabelIndex: 0,
      
      // 生成相关
      generating: false,
      generateProgress: 0,
      showProgress: false,
      generatingZip: false,
      zipProgress: 0,
      showZipProgress: false,
      showHiddenForZip: false,
      // 防抖引用
      _debounceTimer: null,
      // 增量导入控制
      _suppressGenerate: false,
      importing: false,
      importProgress: 0,
      showImportProgress: false
    }
  },
  
  computed: {
    totalPages() {
      return this.qrCodePages.length
    },
    
    itemsPerPage() {
      // 单页模式每页只有一个标签
      if (this.config.generateType === 'singlePDF' || this.config.generateType === 'singleImage') {
        return 1
      }
      // A4多标签模式
      return this.config.rows * this.config.cols
    },
    
    // 当前预览的标签
    currentPreviewLabel() {
      return this.qrCodeLabels[this.currentLabelIndex] || null
    }
  },
  
  watch: {
    // 监听配置变化，重新生成标签
    config: {
      handler() {
        this.scheduleGenerateLabels()
      },
      deep: true
    },
    
    // 监听数据变化，重新生成标签
    tableData: {
      handler() {
        this.scheduleGenerateLabels()
      },
      deep: true
    }
  },
  
  mounted() {
    // 初始化测试数据
    this.loadTestData()
  },
  
  methods: {
    // 微让步封装：在大量循环中周期性让出主线程，避免界面卡顿
    microYield() { return new Promise(r => setTimeout(r)) },
    // 防抖触发标签生成，避免频繁修改引发卡顿
    scheduleGenerateLabels() {
      if (this._suppressGenerate) return
      if (this._debounceTimer) clearTimeout(this._debounceTimer)
      this._debounceTimer = setTimeout(() => {
        this.generateLabels()
      }, 250)
    },
    onVirtualCellChange({ rowIndex, field, value }) {
      if (this.tableData[rowIndex]) {
        this.$set(this.tableData[rowIndex], field, value)
        this.updateTableData()
      }
    },
    scrollTopVirtual() {
      const vt = this.$el.querySelector('.vt-body')
      if (vt) vt.scrollTop = 0
    },
    // ========== 数据处理方法 ==========
    
    // 加载测试数据
    loadTestData() {
      this.tableData = [
        { '产品名称': '黑体中文测试', '产品编码': 'TEST001', '规格': '规格1' },
        { '产品名称': '宋体字体测试', '产品编码': 'TEST002', '规格': '规格2' },
        { '产品名称': 'English Test', '产品编码': 'TEST003', '规格': '规格3' },
        { '产品名称': '混合Chinese&English测试', '产品编码': 'TEST004', '规格': '规格4' },
        { '产品名称': '楷体测试效果显示', '产品编码': 'TEST005', '规格': '规格5' },
        { '产品名称': 'Arial字体测试', '产品编码': 'TEST006', '规格': '规格6' },
        { '产品名称': '仿宋体中文字体', '产品编码': 'TEST007', '规格': '规格7' },
        { '产品名称': '微软雅黑测试', '产品编码': 'TEST008', '规格': '规格8' }
      ]
      this.updateDataFields()
      this.setDefaultConfig()
    },
    
    // 更新数据字段
    updateDataFields() {
      if (this.tableData.length > 0) {
        this.dataFields = Object.keys(this.tableData[0])
      } else {
        this.dataFields = []
      }
    },
    
    // 设置默认配置
    setDefaultConfig() {
      if (this.dataFields.length > 0) {
        if (!this.config.qrField || !this.dataFields.includes(this.config.qrField)) {
          this.config.qrField = this.dataFields[0]
        }
        if (!this.config.textField || !this.dataFields.includes(this.config.textField)) {
          this.config.textField = this.dataFields[0]
        }
        if (!this.config.fileNameField || !this.dataFields.includes(this.config.fileNameField)) {
          this.config.fileNameField = this.dataFields[0]
        }
      }
    },
    
    // 添加数据行
    addDataRow() {
      if (this.dataFields.length === 0) {
        this.tableData.push({ '字段1': '', '字段2': '', '字段3': '' })
        this.updateDataFields()
        this.setDefaultConfig()
      } else {
        const newRow = {}
        this.dataFields.forEach(field => {
          newRow[field] = ''
        })
        this.tableData.push(newRow)
      }
    },
    
    // 删除行
    removeRow(index) {
      this.tableData.splice(index, 1)
    },
    
    // 清空数据
    clearData() {
      this.$confirm('确认清空所有数据吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 保留表头结构，只清空数据
        if (this.dataFields.length > 0) {
          // 创建一个空行保持表头结构
          const emptyRow = {}
          this.dataFields.forEach(field => {
            emptyRow[field] = ''
          })
          this.tableData = [emptyRow]
        } else {
          this.tableData = []
        }
        
        this.qrCodeLabels = []
        this.qrCodePages = []
        this.currentLabelIndex = 0 // 重置当前标签索引
        
        // 不重置字段配置，保持用户的选择
        // this.config.qrField = ''
        // this.config.textField = ''
        
        this.$message.success('数据已清空')
      })
    },
    
    // Excel上传处理
    handleExcelUpload(file) {
      const reader = new FileReader()
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })
          const firstSheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[firstSheetName]
          const jsonData = XLSX.utils.sheet_to_json(worksheet)
          if (jsonData.length === 0) {
            this.$message.warning('Excel文件中没有数据')
            return
          }
          this._suppressGenerate = true
          this.importing = true
          this.showImportProgress = true
          this.importProgress = 0
          this.tableData = []
          this.dataFields = Object.keys(jsonData[0])
          this.setDefaultConfig()
          const total = jsonData.length
          const chunkSize = this.IMPORT_CHUNK_SIZE
          let imported = 0
          for (let i = 0; i < total; i += chunkSize) {
            const chunk = jsonData.slice(i, i + chunkSize)
            Array.prototype.push.apply(this.tableData, chunk)
            imported += chunk.length
            this.importProgress = Math.min(100, Math.round((imported / total) * 100))
            if ((i / chunkSize) % this.IMPORT_YIELD_INTERVAL === 0) await this.microYield()
          }
          this._suppressGenerate = false
          this.generateLabels()
          this.importProgress = 100
          setTimeout(()=>{ this.showImportProgress = false }, 600)
          this.$message.success(`成功增量导入 ${total} 条数据`)
        } catch (error) {
          console.error('Excel解析错误:', error)
          this.$message.error('Excel文件解析失败，请检查文件格式')
        } finally {
          this.importing = false
          this._suppressGenerate = false
        }
      }
      reader.readAsArrayBuffer(file.raw)
    },
    
    // 下载模板
    downloadTemplate() {
      const templateData = [
        { '产品名称': '示例产品1', '产品编码': 'CODE001', '规格': '规格A' },
        { '产品名称': '示例产品2', '产品编码': 'CODE002', '规格': '规格B' }
      ]
      const ws = XLSX.utils.json_to_sheet(templateData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
      XLSX.writeFile(wb, '二维码数据模板.xlsx')
    },
    
    // 更新表格数据
    updateTableData() {
      // 触发重新生成标签
      this.$nextTick(() => {
        this.generateLabels()
      })
    },
    
    // 布局变化处理
    onLayoutChange() {
      this.generateLabels()
    },
    
    // 生成类型切换处理
    onGenerateTypeChange() {
      this.generateLabels()
    },
    
    // 验证文件名是否合法
    validateFileName(fileName) {
      // 检查是否为空
      if (!fileName || fileName.trim() === '') {
        return { valid: false, message: '文件名不能为空' }
      }
      
      // 检查文件名长度
      if (fileName.length > 200) {
        return { valid: false, message: '文件名过长（超过200个字符）' }
      }
      
      // 检查是否包含非法字符
      const invalidChars = /[<>:"/\\|?*\x00-\x1f]/g
      if (invalidChars.test(fileName)) {
        return { valid: false, message: '文件名包含非法字符：< > : " / \\ | ? * 或控制字符' }
      }
      
      // 检查是否为保留文件名
      const reservedNames = [
        'CON', 'PRN', 'AUX', 'NUL',
        'COM1', 'COM2', 'COM3', 'COM4', 'COM5', 'COM6', 'COM7', 'COM8', 'COM9',
        'LPT1', 'LPT2', 'LPT3', 'LPT4', 'LPT5', 'LPT6', 'LPT7', 'LPT8', 'LPT9'
      ]
      const nameWithoutExt = fileName.split('.')[0].toUpperCase()
      if (reservedNames.includes(nameWithoutExt)) {
        return { valid: false, message: `文件名"${fileName}"是系统保留名称` }
      }
      
      // 检查是否以点或空格结尾
      if (fileName.endsWith('.') || fileName.endsWith(' ')) {
        return { valid: false, message: '文件名不能以点或空格结尾' }
      }
      
      return { valid: true, message: '' }
    },
    
    // 验证所有文件名
    validateAllFileNames() {
      // 只有在单页图片模式且选择了文件名字段时才进行验证
      if (this.config.generateType !== 'singleImage' || !this.config.fileNameField) {
        return { valid: true, invalidItems: [] }
      }
      
      const invalidItems = []
      
      this.qrCodeLabels.forEach((label, index) => {
        const fileName = String(label.rowData[this.config.fileNameField] || '')
        const validation = this.validateFileName(fileName)
        
        if (!validation.valid) {
          invalidItems.push({
            index: index + 1,
            fileName: fileName,
            message: validation.message
          })
        }
      })
      
      return {
        valid: invalidItems.length === 0,
        invalidItems: invalidItems
      }
    },
    
    // ========== 标签生成方法 ==========
    
    // 生成所有标签数据
    generateLabels() { return this.generateLabelsAsync() },
    async generateLabelsAsync() {
      if (!this.tableData.length || !this.config.qrField || !this.dataFields.includes(this.config.qrField)) {
        this.qrCodeLabels = []
        this.qrCodePages = []
        return
      }
      const result = []
      const total = this.tableData.length
      for (let i = 0; i < total; i++) {
        const row = this.tableData[i]
        const rawQr = row[this.config.qrField]
        const qrStr = rawQr == null ? '' : String(rawQr)
        if (qrStr && qrStr.trim() !== '') {
          result.push({
            id: i,
            qrContent: qrStr,
            textContent: this.config.showText ? String(row[this.config.textField] || '') : '',
            rowData: row
          })
        }
        if (i % 800 === 0) await this.microYield()
      }
      this.qrCodeLabels = result
      this.generatePages()
      this.$nextTick(()=>{ this.generateQRCodes() })
    },
    
    // 分页处理
    generatePages() {
      const itemsPerPage = this.itemsPerPage
      this.qrCodePages = []
      
      for (let i = 0; i < this.qrCodeLabels.length; i += itemsPerPage) {
        this.qrCodePages.push(this.qrCodeLabels.slice(i, i + itemsPerPage))
      }
      
      // 确保当前标签索引有效
      if (this.currentLabelIndex >= this.qrCodeLabels.length) {
        this.currentLabelIndex = Math.max(0, this.qrCodeLabels.length - 1)
      }
    },
    
    // 生成二维码
    async generateQRCodes() {
      try {
        // 为当前预览标签生成二维码
        await this.generateCurrentLabelQRCode()
        
        // 只有在需要导出图片ZIP时才批量生成隐藏二维码
        if (this.showHiddenForZip) {
          await this.generateAllHiddenQRCodes()
        }
      } catch (error) {
        console.error('生成二维码失败:', error)
      }
    },
    
    // 为当前预览标签生成二维码
    async generateCurrentLabelQRCode() {
      if (!this.currentPreviewLabel || !this.currentPreviewLabel.qrContent) return
      
      await this.generateSingleQRCode('previewCanvas', this.currentPreviewLabel.qrContent)
    },
    
    // 为所有隐藏标签生成二维码
    async generateAllHiddenQRCodes() {
      // 使用并行生成提高速度
      const promises = []
      for (let i = 0; i < this.qrCodeLabels.length; i++) {
        const label = this.qrCodeLabels[i]
        if (label.qrContent) {
          promises.push(this.generateSingleQRCode(`hiddenCanvas_${i}`, label.qrContent))
        }
      }
      
      // 分批处理，避免一次性创建过多Promise
      const batchSize = 20 // 每批处理20个
      for (let i = 0; i < promises.length; i += batchSize) {
        const batch = promises.slice(i, i + batchSize)
        await Promise.all(batch)
      }
    },
    
    // 生成单个二维码（用于预览）
    async generateSingleQRCode(canvasRef, content) {
      if (!content) return
      
      const canvasArray = this.$refs[canvasRef]
      if (!canvasArray || canvasArray.length === 0) return
      
      const canvas = canvasArray[0] || canvasArray
      if (!canvas) return
      
      try {
        // 生成高分辨率二维码但保持正确的显示尺寸
        await QRCode.toCanvas(canvas, content, {
          width: this.getQRCodePixelSize(),
          margin: this.config.qrMargin, // 使用配置的边距
          errorCorrectionLevel: this.config.errorCorrectionLevel, // 使用配置的容错率
          color: {
            dark: '#000000',
            light: '#00000000' // 完全透明的背景
          }
        })
        
        // 确保Canvas的CSS尺寸正确和背景透明
        const displaySizeMM = this.config.qrSize
        canvas.style.width = displaySizeMM + 'mm'
        canvas.style.height = displaySizeMM + 'mm'
        canvas.style.backgroundColor = 'transparent'
        canvas.style.height = displaySizeMM + 'mm'
      } catch (error) {
        console.error('生成二维码失败:', error)
      }
    },
    
    // ========== 样式计算方法 ==========
    
    // 获取标签样式（用于PDF生成）
    getLabelStyle() {
      const labelWidth = 100 / this.config.cols
      const labelHeight = 100 / this.config.rows
      
      return {
        width: `${labelWidth}%`,
        height: `${labelHeight}%`,
        border: this.config.showBorder ? `1px solid ${this.config.borderColor}` : 'none',
        backgroundColor: this.config.showBackground ? this.config.backgroundColor : 'transparent',
        boxSizing: 'border-box',
        padding: '5mm',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    },
    
    // 获取单个标签样式（用于预览）
    getSingleLabelStyle() {
      // 根据生成类型选择尺寸
      let width, height, padding
      if (this.config.generateType === 'singlePDF' || this.config.generateType === 'singleImage') {
        width = this.config.singlePageWidth + 'mm'
        height = this.config.singlePageHeight + 'mm'
        padding = '5mm'
      } else {
        width = '100mm' // A4多标签模式的固定预览尺寸
        height = '60mm'
        padding = '10mm'
      }
      
      return {
        width: width,
        height: height,
        border: this.config.showBorder ? `1px solid ${this.config.borderColor}` : '1px solid #ddd',
        backgroundColor: this.config.showBackground ? this.config.backgroundColor : 'white',
        boxSizing: 'border-box',
        padding: padding,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
        borderRadius: '4px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }
    },
    
    // 获取隐藏标签样式（用于PDF生成）- 与预览标签完全一致
    getHiddenLabelStyle() {
      // 根据生成类型选择尺寸
      let width, height, padding
      if (this.config.generateType === 'singlePDF' || this.config.generateType === 'singleImage') {
        width = this.config.singlePageWidth + 'mm'
        height = this.config.singlePageHeight + 'mm'
        padding = '5mm'
      } else {
        width = '80mm' // A4多标签模式的固定尺寸
        height = '50mm'
        padding = '5mm'
      }
      
      return {
        width: width,
        height: height,
        border: this.config.showBorder ? `1px solid ${this.config.borderColor}` : 'none',
        backgroundColor: this.config.showBackground ? this.config.backgroundColor : 'transparent',
        boxSizing: 'border-box',
        padding: padding,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // 确保渲染一致性
        fontSize: '14px',
        fontFamily: 'Arial, "Microsoft YaHei", "SimSun", sans-serif'
      }
    },
    
    // 获取标签类名
    getLabelClass() {
      return {
        'text-top-layout': this.config.textPosition === 'top',
        'text-bottom-layout': this.config.textPosition === 'bottom',
        'text-left-layout': this.config.textPosition === 'left',
        'text-right-layout': this.config.textPosition === 'right'
      }
    },
    
    // 获取内容样式
    getContentStyle() {
      return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // 移除 flexDirection，让CSS类来控制方向
        gap: `${this.config.textSpacing}mm`
      }
    },
    
    // 获取文字样式
    getTextStyle() {
      const baseStyle = {
        fontSize: `${this.config.fontSize}mm`,
        color: '#000000', // 与二维码颜色一致
        textAlign: 'center',
        wordBreak: 'break-all',
        lineHeight: '1.2'
      }
      
      // 左右位置时使用竖直文字
      if (['left', 'right'].includes(this.config.textPosition)) {
        return {
          ...baseStyle,
          writingMode: 'vertical-rl',
          textOrientation: 'upright',
          maxWidth: '15mm',
          whiteSpace: 'nowrap',
          letterSpacing: '1px'
        }
      }
      
      return baseStyle
    },
    
    // 获取二维码像素大小
    getQRCodePixelSize() {
      // 使用适中的像素密度提升清晰度，但不影响显示尺寸
      return Math.round(this.config.qrSize * 8) // 每毫米8像素，提供清晰度
    },
    
    // ========== 预览控制方法 ==========
    
    // 上一个标签
    prevLabel() {
      if (this.currentLabelIndex > 0) {
        this.currentLabelIndex--
        this.$nextTick(() => {
          this.generateCurrentLabelQRCode()
        })
      }
    },
    
    // 下一个标签
    nextLabel() {
      if (this.currentLabelIndex < this.qrCodeLabels.length - 1) {
        this.currentLabelIndex++
        this.$nextTick(() => {
          this.generateCurrentLabelQRCode()
        })
      }
    },
    
    // 开始制作（根据生成类型选择对应的功能）
    startGenerate() {
      if (this.config.generateType === 'singleImage') {
        this.generateZipImages()
      } else {
        // multiA4 和 singlePDF 都生成PDF
        this.generatePDF()
      }
    },
    
    // ========== PDF生成方法 ==========
    
    // 生成PDF
    async generatePDF() {
      if (!this.qrCodeLabels.length) {
        this.$message.warning('请先添加数据')
        return
      }
      
      // 立即显示进度弹窗
      this.generating = true
      this.showProgress = true
      this.generateProgress = 0
      
      try {
        const totalLabels = this.qrCodeLabels.length
        // 步骤1: 准备字体 (0-15%)，使用全局缓存避免重复下载
        this.generateProgress = 5
        const pdfDoc = await PDFDocument.create()
        pdfDoc.registerFontkit(fontkit)
        if (!window.__sharedFontBytes) {
          window.__sharedFontBytes = await fetch(sourceHanFontUrl).then(r => r.arrayBuffer())
        }
        const customFont = await pdfDoc.embedFont(window.__sharedFontBytes, { subset: true })
        this.generateProgress = 15

        // 步骤2: 创建二维码图片缓存 (15-35%)，分批让出主线程
        const qrImageCache = []
        const dedupeMap = new Map() // key: qrContent -> pdfImage
        for (let i = 0; i < totalLabels; i++) {
          const label = this.qrCodeLabels[i]
          try {
            let pdfImage = dedupeMap.get(label.qrContent)
            if (!pdfImage) {
              const dataUrl = await this.getQRDataUrl(label.qrContent)
              const imgBytes = this.dataURLToUint8Array(dataUrl)
              pdfImage = await pdfDoc.embedPng(imgBytes)
              dedupeMap.set(label.qrContent, pdfImage)
            }
            qrImageCache.push({ image: pdfImage, label })
          } catch (e) {
            console.warn('二维码缓存失败 index=', i, e)
          }
          if (i % this.YIELD_ITEM_INTERVAL === 0) await this.microYield()
          this.generateProgress = 15 + Math.round(((i + 1) / totalLabels) * 20)
        }

        // 步骤3: 分页绘制 (35-90%)，分页间让步
        const itemsPerPage = this.itemsPerPage
        const totalPages = Math.ceil(totalLabels / itemsPerPage)
        for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
          this.drawPDFPage(pdfDoc, pageIndex, qrImageCache, customFont)
          if (pageIndex % this.YIELD_PAGE_INTERVAL === 0) await this.microYield()
          this.generateProgress = 35 + Math.round(((pageIndex + 1) / totalPages) * 55)
        }

        // 步骤4: 保存 (90-100%)
        this.generateProgress = 95
        const pdfBytes = await pdfDoc.save()
        this.generateProgress = 100
        this.downloadPDF(pdfBytes)
        setTimeout(() => { this.showProgress = false }, 800)
        this.$message.success(`PDF生成成功，包含 ${totalLabels} 个标签`)
      } catch (error) {
        console.error('PDF生成失败:', error)
        this.$message.error('PDF生成失败：' + error.message)
        this.showProgress = false
      } finally {
        this.generating = false
      }
    },
    
    // === 新增: 生成二维码 DataURL（用于 PDF 嵌入） ===
    async getQRDataUrl(content) {
      return await QRCode.toDataURL(content, {
        width: this.getQRCodePixelSize(),
        margin: this.config.qrMargin,
        errorCorrectionLevel: this.config.errorCorrectionLevel,
        color: { dark: '#000000', light: '#00000000' }
      })
    },

    // mm 转 pt
    mmToPt(mm) { return mm * 2.834645669 },

    // HEX 转 rgb
    hexToPdfRgb(hex) {
      if (!hex) return rgb(0,0,0)
      const h = hex.replace('#','')
      if (h.length === 3) {
        const r = parseInt(h[0]+h[0],16)/255
        const g = parseInt(h[1]+h[1],16)/255
        const b = parseInt(h[2]+h[2],16)/255
        return rgb(r,g,b)
      }
      if (h.length === 6) {
        const r = parseInt(h.substring(0,2),16)/255
        const g = parseInt(h.substring(2,4),16)/255
        const b = parseInt(h.substring(4,6),16)/255
        return rgb(r,g,b)
      }
      return rgb(0,0,0)
    },

    // 拆分文本为多行
    wrapText(text, font, fontSizePt, maxWidthPt) {
      if (!text) return []
      const chars = text.split('')
      let line = ''
      const lines = []
      for (const ch of chars) {
        const test = line + ch
        const width = font.widthOfTextAtSize(test, fontSizePt)
        if (width > maxWidthPt && line) {
          lines.push(line)
          line = ch
        } else {
          line = test
        }
      }
      if (line) lines.push(line)
      return lines
    },

    // 绘制某一页
    drawPDFPage(pdfDoc, pageIndex, cache, font) {
      let pageWidthPt, pageHeightPt
      let page
      if (this.config.generateType === 'singlePDF') {
        pageWidthPt = this.mmToPt(this.config.singlePageWidth)
        pageHeightPt = this.mmToPt(this.config.singlePageHeight)
        page = pdfDoc.addPage([pageWidthPt, pageHeightPt])
      } else {
        pageWidthPt = 595.28; pageHeightPt = 841.89
        page = pdfDoc.addPage([pageWidthPt, pageHeightPt])
      }

      const itemsPerPage = this.itemsPerPage
      const start = pageIndex * itemsPerPage
      const end = Math.min(start + itemsPerPage, this.qrCodeLabels.length)

      if (this.config.generateType === 'singlePDF') {
        if (start < end) {
          this.drawSingleLabelOnPage(page, cache[start], 0, 0, pageWidthPt, pageHeightPt, font)
        }
      } else {
        const margin = 20 // pt
        const availableWidth = pageWidthPt - margin * 2
        const availableHeight = pageHeightPt - margin * 2
        const itemWidth = availableWidth / this.config.cols
        const itemHeight = availableHeight / this.config.rows
        for (let i = start; i < end; i++) {
          const idxInPage = i - start
            const row = Math.floor(idxInPage / this.config.cols)
          const col = idxInPage % this.config.cols
          const x = margin + col * itemWidth
          const y = pageHeightPt - margin - (row + 1) * itemHeight
          this.drawSingleLabelOnPage(page, cache[i], x, y, itemWidth, itemHeight, font)
        }
      }
    },

    // 绘制单个标签
    drawSingleLabelOnPage(page, cacheItem, x, y, w, h, font) {
      if (!cacheItem) return
      const { image, label } = cacheItem
      const qrSizePt = this.mmToPt(this.config.qrSize)
      const fontSizePt = this.mmToPt(this.config.fontSize)
      const gapPt = this.mmToPt(this.config.textSpacing)
      const hasText = this.config.showText && label.textContent
      const text = hasText ? label.textContent : ''

      // 背景与边框
      if (this.config.showBackground) {
        page.drawRectangle({ x, y, width: w, height: h, color: this.hexToPdfRgb(this.config.backgroundColor) })
      }
      if (this.config.showBorder) {
        page.drawRectangle({ x, y, width: w, height: h, borderWidth: 1, borderColor: this.hexToPdfRgb(this.config.borderColor) })
      }

      if (!hasText) {
        // 居中放置二维码
        const qx = x + (w - qrSizePt) / 2
        const qy = y + (h - qrSizePt) / 2
        page.drawImage(image, { x: qx, y: qy, width: qrSizePt, height: qrSizePt })
        return
      }

      // 文本位置处理
      const position = this.config.textPosition
      if (position === 'left' || position === 'right') {
        // 竖排文本：按字符竖直排列
        const chars = text.split('')
        const totalTextHeight = chars.length * fontSizePt * 1.1
        const blockWidth = fontSizePt * 1.1
        const totalWidth = blockWidth + gapPt + qrSizePt
        const startX = x + (w - totalWidth) / 2
        const centerY = y + (h + qrSizePt) / 2
        const qrX = position === 'left' ? startX + blockWidth + gapPt : startX
        const qrY = centerY - qrSizePt / 2
        page.drawImage(image, { x: qrX, y: qrY, width: qrSizePt, height: qrSizePt })
        // 文本绘制
        const textX = position === 'left' ? startX : startX + qrSizePt + gapPt
        let charY = qrY + qrSizePt - fontSizePt // 从下到上
        chars.forEach(ch => {
          page.drawText(ch, { x: textX + (blockWidth - font.widthOfTextAtSize(ch, fontSizePt)) / 2, y: charY, size: fontSizePt, font, color: rgb(0,0,0) })
          charY -= fontSizePt * 1.1
        })
        return
      }

      // 上下布局
      const maxTextWidth = w - 8 // padding
      const lines = this.wrapText(text, font, fontSizePt, maxTextWidth)
      const lineHeight = fontSizePt * 1.2
      const textBlockHeight = lines.length * lineHeight
      const totalHeight = textBlockHeight + gapPt + qrSizePt
      let startY = y + (h - totalHeight) / 2

      if (position === 'top') {
        // 文本在上
        // 绘制文本
        lines.forEach((line, idx) => {
          const lineWidth = font.widthOfTextAtSize(line, fontSizePt)
          const tx = x + (w - lineWidth) / 2
          const ty = startY + textBlockHeight - (idx + 1) * lineHeight + (lineHeight - fontSizePt)
          page.drawText(line, { x: tx, y: ty, size: fontSizePt, font, color: rgb(0,0,0) })
        })
        const qrX = x + (w - qrSizePt) / 2
        const qrY = startY + textBlockHeight + gapPt
        page.drawImage(image, { x: qrX, y: qrY, width: qrSizePt, height: qrSizePt })
      } else { // bottom
        // 二维码在上
        const qrX = x + (w - qrSizePt) / 2
        const qrY = startY + textBlockHeight + gapPt
        // actually bottom meaning text below -> adjust: swap order
        // 重新计算: totalHeight = qr + gap + textBlock
        startY = y + (h - totalHeight) / 2
        // 二维码先画
        const qrTopY = startY + textBlockHeight + gapPt
        page.drawImage(image, { x: qrX, y: qrTopY, width: qrSizePt, height: qrSizePt })
        // 文本块起始 Y
        lines.forEach((line, idx) => {
          const lineWidth = font.widthOfTextAtSize(line, fontSizePt)
          const tx = x + (w - lineWidth) / 2
          const ty = startY + textBlockHeight - (idx + 1) * lineHeight + (lineHeight - fontSizePt)
          page.drawText(line, { x: tx, y: ty, size: fontSizePt, font, color: rgb(0,0,0) })
        })
      }
    },
    
    // DataURL转Uint8Array
    dataURLToUint8Array(dataURL) {
      const base64String = dataURL.split(',')[1]
      const binaryString = atob(base64String)
      const bytes = new Uint8Array(binaryString.length)
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }
      return bytes
    },
    
    // 下载PDF
    downloadPDF(pdfBytes) {
      const blob = new Blob([pdfBytes], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      
      // 根据生成类型生成不同的文件名
      const modeText = this.config.generateType === 'singlePDF' ? '单页标签' : 'A4多标签'
      const timestamp = new Date().getTime()
      a.download = `${modeText}_批量二维码_${timestamp}.pdf`
      
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },

    // ========== 图片ZIP生成方法 ==========
    
    // 生成单张图片并打包到ZIP
    async generateZipImages() {
      if (!this.qrCodeLabels.length) {
        this.$message.warning('请先添加数据')
        return
      }
      
      // 验证文件名（如果选择了文件名字段）
      if (this.config.fileNameField) {
        const validation = this.validateAllFileNames()
        if (!validation.valid) {
          let errorMessage = '以下文件名格式不正确：\n\n'
          validation.invalidItems.slice(0, 5).forEach(item => {
            errorMessage += `第${item.index}行："${item.fileName}" - ${item.message}\n`
          })
          
          if (validation.invalidItems.length > 5) {
            errorMessage += `\n还有 ${validation.invalidItems.length - 5} 个文件名存在问题...`
          }
          
          this.$alert(errorMessage, '文件名验证失败', {
            confirmButtonText: '确定',
            type: 'error',
            customClass: 'filename-validation-alert'
          })
          return
        }
      }
      
      this.generatingZip = true
      this.showZipProgress = true
      this.zipProgress = 0
      // 启用隐藏标签以渲染所有Canvas
      this.showHiddenForZip = true
      await this.$nextTick()
      
      try {
        // 步骤1: 渲染二维码 (0-20%)
        this.zipProgress = 10
        await this.generateAllHiddenQRCodes()
        
        // 步骤2: 创建ZIP实例 (20-30%)
        this.zipProgress = 20
        const zip = new JSZip()
        const folder = zip.folder('二维码图片')
        
        // 步骤3: 批量生成图片并添加到ZIP (30-90%)
        const totalLabels = this.qrCodeLabels.length
        for (let i = 0; i < totalLabels; i++) {
          const label = this.qrCodeLabels[i]
          const hiddenElement = this.$refs[`hiddenLabel_${i}`]
          
          if (hiddenElement && (hiddenElement[0] || hiddenElement)) {
            const element = hiddenElement[0] || hiddenElement
            
            try {
              // 使用html2canvas将DOM转换为图片
              const canvas = await html2canvas(element, {
                backgroundColor: this.config.showBackground ? this.config.backgroundColor : 'transparent',
                scale: 3, // 高分辨率
                useCORS: true,
                allowTaint: true,
                width: element.offsetWidth,
                height: element.offsetHeight,
                logging: false,
                removeContainer: true
              })
              
              // 将Canvas转换为Blob
              const blob = await new Promise(resolve => {
                canvas.toBlob(resolve, 'image/png', 1.0)
              })
              
              // 生成文件名
              const fileName = this.generateImageFileName(label, i)
              
              // 添加到ZIP
              folder.file(fileName, blob)
              
            } catch (error) {
              console.error(`生成标签 ${i + 1} 图片失败:`, error)
            }
          }
          
          // 更新进度 (30-90%)
          const progress = 30 + Math.round(((i + 1) / totalLabels) * 60)
          this.zipProgress = progress
          
          // 给UI更新的时间
          await this.$nextTick()
        }
        
        // 步骤4: 生成ZIP文件并下载 (90-100%)
        this.zipProgress = 95
        const zipBlob = await zip.generateAsync({ type: 'blob' })
        const timestamp = new Date().getTime()
        const zipFileName = `批量二维码_${timestamp}.zip`
        
        this.zipProgress = 100
        saveAs(zipBlob, zipFileName)
        
        // 延迟隐藏进度条
        setTimeout(() => {
          this.showZipProgress = false
        }, 1500)
        
        this.$message.success(`成功生成包含 ${this.qrCodeLabels.length} 张图片的ZIP文件`)
        
      } catch (error) {
        console.error('ZIP生成失败:', error)
        this.$message.error('ZIP生成失败：' + error.message)
        this.showZipProgress = false
      } finally {
        this.generatingZip = false
        // 释放隐藏DOM减少内存占用
        this.showHiddenForZip = false
      }
    },
    
    // 生成图片文件名
    generateImageFileName(label, index) {
      let fileName
      
      // 如果有自定义文件名字段
      if (this.config.fileNameField && label.rowData[this.config.fileNameField]) {
        fileName = String(label.rowData[this.config.fileNameField]).trim()
        
        // 如果自定义文件名已经包含扩展名，就移除它
        if (fileName.toLowerCase().endsWith('.png') || 
            fileName.toLowerCase().endsWith('.jpg') || 
            fileName.toLowerCase().endsWith('.jpeg')) {
          fileName = fileName.substring(0, fileName.lastIndexOf('.'))
        }
      } else {
        // 使用默认的文件名生成逻辑
        fileName = label.qrContent || `二维码_${index + 1}`
        
        // 替换文件名中的特殊字符
        fileName = fileName.replace(/[<>:"/\\|?*]/g, '_')
        
        // 如果有文字内容且与二维码内容不同，则添加到文件名中
        if (this.config.showText && label.textContent && label.textContent !== label.qrContent) {
          const textContent = label.textContent.replace(/[<>:"/\\|?*]/g, '_')
          fileName += `_${textContent}`
        }
      }
      
      // 确保文件名不为空
      if (!fileName || fileName.trim() === '') {
        fileName = `二维码_${index + 1}`
      }
      
      // 限制文件名长度
      if (fileName.length > 100) {
        fileName = fileName.substring(0, 100)
      }
      
      return `${fileName}.png`
    }
  }
}
</script>

<style scoped>
.batch-qrcode-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 30px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
}

.page-header h1 {
  margin: 10px 0;
  font-size: 28px;
  font-weight: bold;
}

.page-header p {
  margin: 0;
  opacity: 0.9;
}

.main-config-section {
  margin-bottom: 20px;
}

.clearfix {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-buttons {
  display: flex;
  gap: 8px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ed;
}

.section-title {
  font-weight: bold;
  color: #303133;
  font-size: 16px;
}

.data-actions {
  display: flex;
  gap: 8px;
}

.config-group {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #fafbfc;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
}

.group-title {
  margin: 0 0 15px 0;
  color: #606266;
  font-size: 14px;
  font-weight: bold;
  padding-bottom: 8px;
  border-bottom: 1px solid #e4e7ed;
}

.preview-container {
  position: relative;
  background: #f8f9fa;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  padding: 30px;
  margin: 0 auto;
  max-width: 800px;
  min-height: 400px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.single-label-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.label-info {
  text-align: center;
  background: white;
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
  max-width: 400px;
}

.label-info p {
  margin: 8px 0;
  color: #606266;
}

.label-info strong {
  color: #303133;
}

.preview-page {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  height: 100%;
}

.qr-label-item {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.qr-content {
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-code-container {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  background: transparent !important;
}

.qr-code-container canvas {
  background: transparent !important;
}

.qr-text {
  text-align: center;
  font-weight: bold;
  flex-shrink: 0;
}

/* 文字位置布局 */
.text-top-layout .qr-content {
  flex-direction: column-reverse; /* 文字在上，二维码在下 */
}

.text-bottom-layout .qr-content {
  flex-direction: column; /* 二维码在上，文字在下 */
}

/* 左右位置时的布局 */
.text-left-layout .qr-content {
  flex-direction: row-reverse; /* 文字在左，二维码在右 */
}

.text-right-layout .qr-content {
  flex-direction: row; /* 二维码在左，文字在右 */
}

/* 左右位置时的竖直文字样式 */
.text-left-layout .qr-text,
.text-right-layout .qr-text {
  writing-mode: vertical-rl !important;
  text-orientation: upright !important;
  text-align: center !important;
  line-height: 1.2 !important;
  white-space: nowrap !important;
  letter-spacing: 1px !important;
}

.page-info {
  position: absolute;
  bottom: 5px;
  right: 10px;
  font-size: 12px;
  color: #999;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .batch-qrcode-container {
    padding: 10px;
  }

  .page-header {
    padding: 20px 10px;
  }

  .page-header h1 {
    font-size: 24px;
  }

  .preview-container {
    width: 100%;
    padding: 10mm;
  }
}

/* 文件名验证错误对话框样式 */
.filename-validation-alert .el-message-box__message {
  white-space: pre-line;
  font-family: monospace;
  max-height: 300px;
  overflow-y: auto;
}
</style>
